import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Search, Download, X, Phone, Mail, MessageCircle, FileText, Trash2, Users, Clock, CalendarDays, AlertTriangle, ChevronRight, Filter, Bell, BellRing, RefreshCw, Settings, Edit3, KeyRound } from 'lucide-react';
import { Link } from 'react-router-dom';
import './Admin.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = ['Neu', 'Kontaktiert', 'In Bearbeitung', 'Angebot gesendet', 'Gebucht', 'Abgelehnt'];

const statusClass = (status) => {
  const map = {
    'Neu': 'status-neu',
    'Kontaktiert': 'status-kontaktiert',
    'In Bearbeitung': 'status-in-bearbeitung',
    'Angebot gesendet': 'status-angebot-gesendet',
    'Gebucht': 'status-gebucht',
    'Abgelehnt': 'status-abgelehnt',
  };
  return map[status] || 'status-neu';
};

const formatDate = (ts) => {
  if (!ts) return '-';
  try {
    const d = new Date(ts);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ts; }
};

const adminFetch = async (endpoint, options = {}) => {
  const token = localStorage.getItem('adminToken');
  if (!token) {
    window.location.href = '/admin/login';
    return null;
  }
  const res = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });
  if (res.status === 401) {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    window.location.href = '/admin/login';
    return null;
  }
  return res;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [flash, setFlash] = useState('');
  const [noteLoading, setNoteLoading] = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [upcomingToday, setUpcomingToday] = useState('');
  const [upcomingFilter, setUpcomingFilter] = useState(true);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(null);
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) { navigate('/admin/login'); return; }
  }, [navigate]);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { if (meta.parentNode) meta.parentNode.removeChild(meta); };
  }, []);

  const showFlash = (msg) => {
    setFlash(msg);
    setTimeout(() => setFlash(''), 3000);
  };

  const fetchStats = useCallback(async () => {
    const res = await adminFetch('/api/admin/stats');
    if (res && res.ok) {
      const data = await res.json();
      setStats(data);
    }
  }, []);

  const fetchUpcomingEvents = useCallback(async () => {
    const params = new URLSearchParams();
    if (upcomingFilter) params.set('statusFilter', 'In Bearbeitung,Angebot gesendet,Gebucht');
    const qs = params.toString();
    const res = await adminFetch(`/api/admin/upcoming-events${qs ? '?' + qs : ''}`);
    if (res && res.ok) {
      const data = await res.json();
      setUpcomingEvents(data.events || []);
      setUpcomingToday(data.today || '');
    }
  }, [upcomingFilter]);

  const fetchLeads = useCallback(async () => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (statusFilter) params.set('status', statusFilter);
    const qs = params.toString();
    const res = await adminFetch(`/api/admin/leads${qs ? '?' + qs : ''}`);
    if (res && res.ok) {
      const data = await res.json();
      setLeads(data);
    }
    setLoading(false);
  }, [search, statusFilter]);

  useEffect(() => {
    fetchStats();
    fetchLeads();
    fetchUpcomingEvents();
  }, [fetchStats, fetchLeads, fetchUpcomingEvents]);

  const handleStatusChange = async (leadId, newStatus) => {
    const res = await adminFetch(`/api/admin/leads/${leadId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status: newStatus }),
    });
    if (res && res.ok) {
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead(prev => ({ ...prev, status: newStatus }));
      }
      fetchStats();
      showFlash(`Status auf "${newStatus}" geaendert`);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim() || !selectedLead) return;
    setNoteLoading(true);
    const res = await adminFetch(`/api/admin/leads/${selectedLead.id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ text: newNote.trim() }),
    });
    if (res && res.ok) {
      const data = await res.json();
      const updatedNotes = [...(selectedLead.notes || []), data.note];
      setSelectedLead(prev => ({ ...prev, notes: updatedNotes }));
      setLeads(prev => prev.map(l => l.id === selectedLead.id ? { ...l, notes: updatedNotes } : l));
      setNewNote('');
      showFlash('Notiz hinzugefuegt');
    }
    setNoteLoading(false);
  };

  const handleDeleteNote = async (noteId) => {
    if (!selectedLead) return;
    const res = await adminFetch(`/api/admin/leads/${selectedLead.id}/notes/${noteId}`, {
      method: 'DELETE',
    });
    if (res && res.ok) {
      const updatedNotes = (selectedLead.notes || []).filter(n => n.id !== noteId);
      setSelectedLead(prev => ({ ...prev, notes: updatedNotes }));
      setLeads(prev => prev.map(l => l.id === selectedLead.id ? { ...l, notes: updatedNotes } : l));
      showFlash('Notiz geloescht');
    }
  };

  const handleExportCSV = async () => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (statusFilter) params.set('status', statusFilter);
    const qs = params.toString();
    const token = localStorage.getItem('adminToken');
    const res = await fetch(`${API_URL}/api/admin/leads/export/csv${qs ? '?' + qs : ''}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_export_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showFlash('CSV Export erfolgreich');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  const handleManualReminderCheck = async () => {
    setReminderLoading(true);
    const res = await adminFetch('/api/admin/reminders/check', { method: 'POST' });
    if (res && res.ok) {
      const data = await res.json();
      const total = (data.results?.['7day'] || []).length;
      showFlash(total > 0 ? `${total} Erinnerung(en) gesendet` : 'Keine faelligen Erinnerungen');
      fetchLeads();
      fetchUpcomingEvents();
    }
    setReminderLoading(false);
  };

  const handleDeleteLead = async (leadId) => {
    const res = await adminFetch(`/api/admin/leads/${leadId}`, { method: 'DELETE' });
    if (res && res.ok) {
      setLeads(prev => prev.filter(l => l.id !== leadId));
      if (selectedLead && selectedLead.id === leadId) setSelectedLead(null);
      setSelectedIds(prev => { const n = new Set(prev); n.delete(leadId); return n; });
      fetchStats();
      showFlash('Lead endgueltig geloescht');
    }
    setConfirmDelete(null);
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    const res = await adminFetch('/api/admin/leads/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ lead_ids: ids }),
    });
    if (res && res.ok) {
      const data = await res.json();
      setLeads(prev => prev.filter(l => !selectedIds.has(l.id)));
      if (selectedLead && selectedIds.has(selectedLead.id)) setSelectedLead(null);
      setSelectedIds(new Set());
      fetchStats();
      showFlash(`${data.deletedCount} Lead(s) endgueltig geloescht`);
    }
    setConfirmDelete(null);
  };

  const toggleSelectLead = (leadId) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(leadId)) n.delete(leadId); else n.add(leadId);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === leads.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(leads.map(l => l.id)));
    }
  };

  const openWhatsApp = (lead) => {
    const phone = (lead.phone || '').replace(/[^0-9]/g, '');
    const phoneFormatted = phone.startsWith('0') ? '49' + phone.slice(1) : phone;
    const msg = encodeURIComponent(
      `Hallo ${lead.name}, vielen Dank für Ihre Anfrage bei BD Event Location. Wir melden uns gerne bei Ihnen bezüglich Ihres gewünschten Termins.`
    );
    window.open(`https://wa.me/${phoneFormatted}?text=${msg}`, '_blank');
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-loading" data-testid="admin-loading">
          <div className="admin-loading-spinner"></div>
          Dashboard wird geladen...
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      {flash && <div className="admin-success-flash" data-testid="success-flash">{flash}</div>}

      {/* Top Bar */}
      <div className="admin-topbar" data-testid="admin-topbar">
        <div className="admin-topbar-left">
          <span className="admin-topbar-logo">BD Event Location</span>
          <span className="admin-topbar-badge">Admin</span>
        </div>
        <div className="admin-topbar-right">
          <div className="admin-nav-links">
            <Link to="/admin/content" className="admin-nav-btn" data-testid="nav-content"><Edit3 size={14} /> Inhalte</Link>
            <Link to="/admin/settings" className="admin-nav-btn" data-testid="nav-settings"><Settings size={14} /> Einstellungen</Link>
            <Link to="/admin/change-password" className="admin-nav-btn" data-testid="nav-change-password"><KeyRound size={14} /> Passwort</Link>
          </div>
          <span className="admin-topbar-user">{adminUser.username || 'Admin'}</span>
          <button className="admin-logout-btn" onClick={handleLogout} data-testid="logout-button">
            <LogOut size={16} />
            Abmelden
          </button>
        </div>
      </div>

      <div className="admin-main">
        {/* KPI Cards */}
        {stats && (
          <div className="admin-kpi-grid" data-testid="kpi-grid">
            <div className="admin-kpi-card kpi-total">
              <div className="admin-kpi-label">Alle Leads</div>
              <div className="admin-kpi-value" data-testid="kpi-total">{stats.total}</div>
            </div>
            <div className="admin-kpi-card kpi-neu">
              <div className="admin-kpi-label">Neue</div>
              <div className="admin-kpi-value" data-testid="kpi-neu">{stats.statusCounts?.['Neu'] || 0}</div>
            </div>
            <div className="admin-kpi-card kpi-kontaktiert">
              <div className="admin-kpi-label">Kontaktiert</div>
              <div className="admin-kpi-value" data-testid="kpi-kontaktiert">{stats.statusCounts?.['Kontaktiert'] || 0}</div>
            </div>
            <div className="admin-kpi-card kpi-gebucht">
              <div className="admin-kpi-label">Gebucht</div>
              <div className="admin-kpi-value" data-testid="kpi-gebucht">{stats.statusCounts?.['Gebucht'] || 0}</div>
            </div>
            <div className="admin-kpi-card kpi-abgelehnt">
              <div className="admin-kpi-label">Abgelehnt</div>
              <div className="admin-kpi-value" data-testid="kpi-abgelehnt">{stats.statusCounts?.['Abgelehnt'] || 0}</div>
            </div>
            <div className="admin-kpi-card kpi-week">
              <div className="admin-kpi-label">Letzte 7 Tage</div>
              <div className="admin-kpi-value" data-testid="kpi-week">{stats.last7Days}</div>
            </div>
            <div className="admin-kpi-card kpi-month">
              <div className="admin-kpi-label">Diesen Monat</div>
              <div className="admin-kpi-value" data-testid="kpi-month">{stats.thisMonth}</div>
            </div>
          </div>
        )}

        {/* Upcoming Events Widget */}
        <div className="upcoming-events-section" data-testid="upcoming-events-section">
          <div className="upcoming-events-header">
            <div className="upcoming-events-title">
              <CalendarDays size={20} />
              <h2>Anstehende Events</h2>
              <span className="upcoming-events-count">{upcomingEvents.length}</span>
            </div>
            <button
              className={`upcoming-filter-toggle ${upcomingFilter ? 'active' : ''}`}
              onClick={() => setUpcomingFilter(!upcomingFilter)}
              data-testid="upcoming-filter-toggle"
              title={upcomingFilter ? 'Alle Status anzeigen' : 'Nur relevante Status'}
            >
              <Filter size={14} />
              {upcomingFilter ? 'Relevante Status' : 'Alle Status'}
            </button>
            <button
              className="upcoming-filter-toggle"
              onClick={handleManualReminderCheck}
              disabled={reminderLoading}
              data-testid="manual-reminder-check"
              title="Erinnerungen jetzt pruefen und senden"
            >
              {reminderLoading ? <RefreshCw size={14} className="spinning" /> : <Bell size={14} />}
              {reminderLoading ? 'Pruefe...' : 'Erinnerungen pruefen'}
            </button>
          </div>

          {upcomingEvents.length === 0 ? (
            <div className="upcoming-empty" data-testid="upcoming-empty">
              <CalendarDays size={32} />
              <p>Keine anstehenden Events in den naechsten 30 Tagen{upcomingFilter ? ' (mit relevantem Status)' : ''}.</p>
            </div>
          ) : (
            <div className="upcoming-events-grid" data-testid="upcoming-events-grid">
              {upcomingEvents.map((event) => {
                const eventDate = new Date(event.eventDate + 'T00:00:00');
                const todayDate = new Date(upcomingToday + 'T00:00:00');
                const diffDays = Math.round((eventDate - todayDate) / (1000 * 60 * 60 * 24));
                const isToday = diffDays === 0;
                const isUrgent = diffDays > 0 && diffDays <= 7;
                const urgencyClass = isToday ? 'event-today' : isUrgent ? 'event-urgent' : '';
                const dateFormatted = eventDate.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
                const notePreview = event.notes?.length > 0 ? event.notes[event.notes.length - 1].text : null;

                return (
                  <div
                    key={event.id}
                    className={`upcoming-event-card ${urgencyClass}`}
                    data-testid={`upcoming-event-${event.id}`}
                  >
                    <div className="upcoming-event-date-col">
                      <div className="upcoming-event-day">{eventDate.getDate()}</div>
                      <div className="upcoming-event-month">{eventDate.toLocaleDateString('de-DE', { month: 'short' })}</div>
                      {isToday && <span className="upcoming-badge-today">Heute</span>}
                      {isUrgent && !isToday && <span className="upcoming-badge-urgent">{diffDays}T</span>}
                    </div>
                    <div className="upcoming-event-info">
                      <div className="upcoming-event-top">
                        <span className="upcoming-event-name">{event.name}</span>
                        <span className={`admin-status-badge ${statusClass(event.status)}`}>{event.status}</span>
                      </div>
                      <div className="upcoming-event-meta">
                        <span>{event.eventType}</span>
                        {event.guestCount && <span>{event.guestCount} Gaeste</span>}
                        <span>{dateFormatted}</span>
                      </div>
                      {notePreview && (
                        <div className="upcoming-event-note" title={notePreview}>
                          {notePreview.length > 60 ? notePreview.slice(0, 60) + '...' : notePreview}
                        </div>
                      )}
                      {event.remindersSent?.['7day'] && (
                        <div className="upcoming-reminder-sent">
                          <BellRing size={12} /> 7-Tage-Erinnerung gesendet
                        </div>
                      )}
                    </div>
                    <div className="upcoming-event-actions" onClick={(e) => e.stopPropagation()}>
                      <button className="upcoming-action-btn" onClick={() => setSelectedLead(event)} title="Details" data-testid={`upcoming-detail-${event.id}`}>
                        <ChevronRight size={16} />
                      </button>
                      <button className="upcoming-action-btn action-wa" onClick={() => openWhatsApp(event)} title="WhatsApp" data-testid={`upcoming-wa-${event.id}`}>
                        <MessageCircle size={16} />
                      </button>
                      <a href={`tel:${event.phone}`} className="upcoming-action-btn action-call" title="Anrufen">
                        <Phone size={16} />
                      </a>
                      <a href={`mailto:${event.email}`} className="upcoming-action-btn action-mail" title="E-Mail">
                        <Mail size={16} />
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div className="admin-toolbar" data-testid="admin-toolbar">
          <div className="admin-search-wrapper">
            <Search size={18} />
            <input
              type="text"
              className="admin-search-input"
              placeholder="Name, Telefon oder E-Mail suchen..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-input"
            />
          </div>
          <select
            className="admin-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="status-filter"
          >
            <option value="">Alle Status</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button className="admin-export-btn" onClick={handleExportCSV} data-testid="export-csv-button">
            <Download size={16} />
            CSV Export
          </button>
          {selectedIds.size > 0 && (
            <button
              className="admin-export-btn"
              style={{background: '#dc3545', color: '#fff'}}
              onClick={() => setConfirmDelete({ type: 'bulk', count: selectedIds.size })}
              data-testid="bulk-delete-button"
            >
              <Trash2 size={16} />
              {selectedIds.size} loeschen
            </button>
          )}
        </div>

        {/* Lead Table */}
        <div className="admin-table-wrapper" data-testid="lead-table-wrapper">
          {leads.length === 0 ? (
            <div className="admin-empty-state" data-testid="empty-state">
              <Users size={48} />
              <h3>Keine Leads gefunden</h3>
              <p>{search || statusFilter ? 'Versuchen Sie andere Suchbegriffe oder Filter.' : 'Noch keine Anfragen eingegangen.'}</p>
            </div>
          ) : (
            <>
              <div className="admin-table-scroll">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th style={{width: 36}}>
                        <input
                          type="checkbox"
                          checked={leads.length > 0 && selectedIds.size === leads.length}
                          onChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </th>
                      <th>Datum</th>
                      <th>Name</th>
                      <th>Kontakt</th>
                      <th>Event</th>
                      <th>Gaeste</th>
                      <th>Status</th>
                      <th>Notizen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr key={lead.id} onClick={() => setSelectedLead(lead)} data-testid={`lead-row-${lead.id}`}>
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(lead.id)}
                            onChange={() => toggleSelectLead(lead.id)}
                            data-testid={`select-lead-${lead.id}`}
                          />
                        </td>
                        <td>{formatDate(lead.timestamp)}</td>
                        <td>
                          <div className="admin-lead-name">{lead.name}</div>
                        </td>
                        <td>
                          <div>{lead.phone}</div>
                          <div className="admin-lead-email">{lead.email}</div>
                        </td>
                        <td>{lead.eventType}</td>
                        <td>{lead.guestCount || '-'}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <select
                            className="admin-status-select"
                            value={lead.status || 'Neu'}
                            onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                            data-testid={`status-select-${lead.id}`}
                          >
                            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td>
                          <span className={`admin-status-badge ${lead.notes?.length ? 'status-kontaktiert' : 'status-neu'}`}>
                            {lead.notes?.length || 0}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="admin-table-footer" data-testid="table-footer">
                {leads.length} {leads.length === 1 ? 'Lead' : 'Leads'} angezeigt
              </div>
            </>
          )}
        </div>
      </div>

      {/* Lead Detail Panel */}
      {selectedLead && (
        <>
          <div className="admin-detail-overlay" onClick={() => setSelectedLead(null)} data-testid="detail-overlay" />
          <div className="admin-detail-panel" data-testid="lead-detail-panel">
            <div className="admin-detail-header">
              <h2>{selectedLead.name}</h2>
              <button className="admin-detail-close" onClick={() => setSelectedLead(null)} data-testid="close-detail">
                <X size={20} />
              </button>
            </div>
            <div className="admin-detail-body">
              {/* Contact Info */}
              <div className="admin-detail-section">
                <h3>Kontaktdaten</h3>
                <div className="admin-detail-field">
                  <span className="admin-detail-label">Name</span>
                  <span className="admin-detail-value">{selectedLead.name}</span>
                </div>
                <div className="admin-detail-field">
                  <span className="admin-detail-label">Telefon</span>
                  <span className="admin-detail-value">
                    <a href={`tel:${selectedLead.phone}`}>{selectedLead.phone}</a>
                  </span>
                </div>
                <div className="admin-detail-field">
                  <span className="admin-detail-label">E-Mail</span>
                  <span className="admin-detail-value">
                    <a href={`mailto:${selectedLead.email}`}>{selectedLead.email}</a>
                  </span>
                </div>
              </div>

              {/* Event Info */}
              <div className="admin-detail-section">
                <h3>Event-Details</h3>
                <div className="admin-detail-field">
                  <span className="admin-detail-label">Event-Typ</span>
                  <span className="admin-detail-value">{selectedLead.eventType || '-'}</span>
                </div>
                <div className="admin-detail-field">
                  <span className="admin-detail-label">Wunschdatum</span>
                  <span className="admin-detail-value">{selectedLead.eventDate || 'Nicht angegeben'}</span>
                </div>
                <div className="admin-detail-field">
                  <span className="admin-detail-label">Anzahl Gaeste</span>
                  <span className="admin-detail-value">{selectedLead.guestCount || 'Nicht angegeben'}</span>
                </div>
                <div className="admin-detail-field">
                  <span className="admin-detail-label">Nachricht</span>
                  <span className="admin-detail-value">{selectedLead.message || 'Keine Nachricht'}</span>
                </div>
              </div>

              {/* Meta */}
              <div className="admin-detail-section">
                <h3>System</h3>
                <div className="admin-detail-field">
                  <span className="admin-detail-label">Eingangsdatum</span>
                  <span className="admin-detail-value">{formatDate(selectedLead.timestamp)}</span>
                </div>
                <div className="admin-detail-field">
                  <span className="admin-detail-label">E-Mail gesendet</span>
                  <span className="admin-detail-value">{selectedLead.emailSent ? 'Ja' : 'Nein'}</span>
                </div>
                <div className="admin-detail-field">
                  <span className="admin-detail-label">Status</span>
                  <select
                    className="admin-status-select"
                    value={selectedLead.status || 'Neu'}
                    onChange={(e) => handleStatusChange(selectedLead.id, e.target.value)}
                    data-testid="detail-status-select"
                  >
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="admin-detail-field">
                  <span className="admin-detail-label">7-Tage-Erinnerung</span>
                  <span className="admin-detail-value" data-testid="reminder-status-7day">
                    {selectedLead.remindersSent?.['7day']
                      ? <span className="reminder-sent-badge"><BellRing size={14} /> Gesendet am {formatDate(selectedLead.remindersSent['7day'])}</span>
                      : <span className="reminder-pending-badge">Noch nicht gesendet</span>
                    }
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="admin-detail-section">
                <h3>Aktionen</h3>
                <div className="admin-detail-actions">
                  <button className="admin-whatsapp-btn" onClick={() => openWhatsApp(selectedLead)} data-testid="whatsapp-button">
                    <MessageCircle size={18} />
                    WhatsApp
                  </button>
                  <a href={`tel:${selectedLead.phone}`} className="admin-call-btn" data-testid="call-button">
                    <Phone size={18} />
                    Anrufen
                  </a>
                  <a href={`mailto:${selectedLead.email}`} className="admin-email-btn" data-testid="email-button">
                    <Mail size={18} />
                    E-Mail
                  </a>
                  <button
                    className="admin-email-btn"
                    style={{background: '#dc3545', borderColor: '#dc3545'}}
                    onClick={() => setConfirmDelete({ type: 'single', leadId: selectedLead.id, leadName: selectedLead.name })}
                    data-testid="delete-lead-button"
                  >
                    <Trash2 size={18} />
                    Loeschen
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div className="admin-detail-section">
                <h3>Interne Notizen</h3>
                {selectedLead.notes && selectedLead.notes.length > 0 ? (
                  <div className="admin-notes-list" data-testid="notes-list">
                    {selectedLead.notes.map((note) => (
                      <div key={note.id} className="admin-note-item" data-testid={`note-${note.id}`}>
                        <p className="admin-note-text">{note.text}</p>
                        <span className="admin-note-meta">
                          {note.author} &middot; {formatDate(note.timestamp)}
                        </span>
                        <button
                          className="admin-note-delete"
                          onClick={() => handleDeleteNote(note.id)}
                          title="Notiz loeschen"
                          data-testid={`delete-note-${note.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="admin-no-notes">Noch keine Notizen vorhanden.</div>
                )}
                <form className="admin-note-form" onSubmit={handleAddNote} data-testid="note-form">
                  <textarea
                    className="admin-note-input"
                    placeholder="Neue Notiz hinzufuegen..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    rows={2}
                    data-testid="note-input"
                  />
                  <button
                    type="submit"
                    className="admin-note-submit"
                    disabled={!newNote.trim() || noteLoading}
                    data-testid="add-note-button"
                  >
                    {noteLoading ? '...' : 'Speichern'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <>
          <div className="admin-detail-overlay" style={{zIndex: 1100}} onClick={() => setConfirmDelete(null)} />
          <div className="admin-detail-panel" style={{zIndex: 1101, maxWidth: 400, padding: 32, textAlign: 'center'}} data-testid="delete-confirm-modal">
            <Trash2 size={40} style={{color: '#dc3545', marginBottom: 16}} />
            <h2 style={{fontSize: 18, marginBottom: 8}}>Lead(s) endgueltig loeschen?</h2>
            <p style={{color: '#666', fontSize: 14, marginBottom: 24}}>
              {confirmDelete.type === 'single'
                ? `"${confirmDelete.leadName}" wird unwiderruflich aus der Datenbank geloescht.`
                : `${confirmDelete.count} Lead(s) werden unwiderruflich aus der Datenbank geloescht.`
              }
            </p>
            <div style={{display: 'flex', gap: 12, justifyContent: 'center'}}>
              <button
                className="admin-export-btn"
                onClick={() => setConfirmDelete(null)}
                data-testid="cancel-delete"
              >
                Abbrechen
              </button>
              <button
                className="admin-export-btn"
                style={{background: '#dc3545', color: '#fff', borderColor: '#dc3545'}}
                onClick={() => confirmDelete.type === 'single' ? handleDeleteLead(confirmDelete.leadId) : handleBulkDelete()}
                data-testid="confirm-delete"
              >
                Endgueltig loeschen
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
